import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import ShowText from '@/components/dueDiligence-page/dueDiligenceRecord/showText';
import { TierDropdown } from '@/components/dueDiligence-page/dueDiligenceRecord/tierDropdown';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { isValidUrl } from '@/shared/utils/url';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CREATE_TENANT_SETTING_OVERRIDE_MUTATION, SYSTEM_SETTINGS_QUERY, UPDATE_SYSTEM_SETTING_MUTATION } from '../../queries';
import { getCacheBustedFileUrl, mapSettingsData } from '../../utils';
import { EmptyTodoRowCell, FieldValueWrapper, InfoRowCell, RowWrapper } from '../catalogBanner';
import { templateStylingColorsItems, templateStylingItems } from './constants';
import { EditAccentColor } from './editModalPages/editAccentColor';
import { EditDisclosureText } from './editModalPages/editDisclosureText';
import { EditFontStyle } from './editModalPages/editFontStyle';
import { EditLogo } from './editModalPages/editLogo';
import { EditWatermark } from './editModalPages/editWatermark';
import { IFirmSettings, IFontStyle, ISetting, ITemplateStylingSettings } from './types';

const getTemplateStylingZeroState = (key: string) => {
  if (key === 'logo') {
    return (
      <LogoZeroState>
        <NoLogoText>No logo has been added.</NoLogoText>
        <LogoSpecifications>
          Logo Specifications:
          <LogoSpecificationsUnorderedList>
            <li>5:1 Ratio (width:height)</li>
            <li>For use on a white background</li>
          </LogoSpecificationsUnorderedList>
        </LogoSpecifications>
      </LogoZeroState>
    );
  }
  return 'To Do';
};

const formatFontColor = (color: string): string => {
  return color.startsWith('#') ? color : `#${color}`;
};

const getTemplateStylingValue = (item: IFirmSettings) => {
  const { key, value } = item;

  switch (key) {
    case 'headerFontStyle': {
      const fontColor = formatFontColor(value.fontColor);
      const displayColor = value.fontColor.startsWith('#') ? value.fontColor : `#${value.fontColor}`;
      return (
        <FontStyleWrapper fontFamily={value.fontFamily} fontColor={fontColor} fontWeight={value.fontWeight}>
          <FontStyleText>{value.fontFamily}</FontStyleText>
          <Separator>,</Separator>
          <ColorPreviewWrapper>
            <ColorPreview bgColor={fontColor} />
            <span>{displayColor}</span>
          </ColorPreviewWrapper>
          <Separator>,</Separator>
          <FontStyleText>{value.fontWeight}</FontStyleText>
        </FontStyleWrapper>
      );
    }
    // case 'bodyFontStyle':
    //   return (
    //     <FieldValueWrapper
    //       style={{
    //         fontFamily: value.fontFamily,
    //         color: `#${value.fontColor}`,
    //         fontWeight: value.fontWeight
    //       }}
    //     >
    //       {value.fontFamily}, {value.fontColor}, {value.fontWeight}
    //     </FieldValueWrapper>
    //   );
    case 'disclosureText':
      return (
        <FieldValueWrapper>
          <ShowText>{value}</ShowText>
        </FieldValueWrapper>
      );
    case 'watermark':
      return isValidUrl(value) ? (
        <ImageWrapper>
          <ImageItem src={getCacheBustedFileUrl(value)} alt="watermark" />
        </ImageWrapper>
      ) : (
        <FieldValueWrapper>{value}</FieldValueWrapper>
      );
    case 'logo':
      return (
        <ImageWrapper>
          <ImageItem src={getCacheBustedFileUrl(value)} alt="logo" />
        </ImageWrapper>
      );
    default:
      return <></>;
  }
};

interface TemplatesSettingsProps {
  backToTitle: string;
  handleClose: () => void;
}

export const TemplatesSettings = ({ backToTitle, handleClose }: TemplatesSettingsProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const [searchParams] = useSearchParams();

  const action = searchParams.get('action') || '';
  const type = searchParams.get('type') || '';
  const editPageKey = searchParams.get('edit') || 'default';

  const [templateStylingData, setTemplateStylingData] = useState<ITemplateStylingSettings>({});
  const [templateStylingSettingsItems, setTemplateStylingSettingsItems] = useState<IFirmSettings[]>([]);
  const [colorsSectionItems, setColorsSectionItems] = useState<IFirmSettings[]>([]);
  const [settingsItemId, setSettingsItemId] = useState<string | null>(null);
  const [currentRow, setCurrentRow] = useState<IFirmSettings | null>(null);
  const [scrollTo, setScrollTo] = useState(0);

  const [createTenantSettingOverride] = useMutation(CREATE_TENANT_SETTING_OVERRIDE_MUTATION);
  const [updateSystemSetting] = useMutation(UPDATE_SYSTEM_SETTING_MUTATION);

  const { loading: systemSettingsLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      data: {
        tenantId: user?.tenant?.id,
        type: 'customization'
      }
    },
    onCompleted: (data) => {
      const templateStylingData = data?.systemSettings?.find((setting: ISetting) => setting.key === 'templateStyling') ?? null;
      if (templateStylingData) {
        if (templateStylingData?.value) {
          try {
            const parsedValue = JSON.parse(templateStylingData.value);
            setTemplateStylingData(parsedValue);

            const mappedTemplateStylingSettings = mapSettingsData(templateStylingItems, parsedValue);
            setTemplateStylingSettingsItems(mappedTemplateStylingSettings);

            const mappedColorsSectionSettings = mapSettingsData(templateStylingColorsItems, parsedValue);
            setColorsSectionItems(mappedColorsSectionSettings);
          } catch (error) {
            const mappedTemplateStylingSettings = mapSettingsData(templateStylingItems);
            setTemplateStylingSettingsItems(mappedTemplateStylingSettings);

            const mappedColorsSectionSettings = mapSettingsData(templateStylingColorsItems);
            setColorsSectionItems(mappedColorsSectionSettings);
          }
        }

        setSettingsItemId(templateStylingData?.id ?? null);
      }
    }
  });

  useEffect(() => {
    if (!templateStylingSettingsItems.length) return;

    const row = templateStylingSettingsItems?.find((item) => item.key === editPageKey);
    setCurrentRow(row || null);
  }, [templateStylingSettingsItems]);

  useEffect(() => {
    if (editPageKey === 'default') {
      window.scrollTo(0, scrollTo);
    }
  }, [editPageKey, systemSettingsLoading]);

  const redirectToEditPage = (row: IFirmSettings) => {
    const queryParams = new URLSearchParams({
      action,
      ...(type && { type }),
      edit: row.key
    });

    navigate(`/settings?${queryParams.toString()}`);
    setScrollTo(window.scrollY);
    setCurrentRow(row);
  };

  const handleCloseEditPage = () => {
    const queryParams = new URLSearchParams({
      action,
      ...(type && { type })
    });

    navigate(`/settings?${queryParams.toString()}`);
    setCurrentRow(null);
  };

  const saveSettingValue = async (key: string, value: string | IFontStyle) => {
    const updatedTemplateStylingData = { ...templateStylingData, [key]: value };
    let updatedSettingId;
    if (settingsItemId) {
      const {
        data: {
          updateSystemSetting: { id }
        }
      } = await updateSystemSetting({
        variables: {
          data: {
            id: settingsItemId,
            value: JSON.stringify(updatedTemplateStylingData),
            description: ''
          }
        }
      });
      updatedSettingId = id;
    } else {
      const {
        data: {
          createTenantSettingOverride: { id }
        }
      } = await createTenantSettingOverride({
        variables: {
          data: {
            tenantId: user?.tenant?.id,
            type: 'customization',
            key: 'templateStyling',
            value: JSON.stringify(updatedTemplateStylingData)
          }
        }
      });
      updatedSettingId = id;
    }

    setSettingsItemId(updatedSettingId);

    const mappedTemplateStylingSettings = mapSettingsData(templateStylingItems, updatedTemplateStylingData);
    setTemplateStylingSettingsItems(mappedTemplateStylingSettings);

    const mappedColorsSectionSettings = mapSettingsData(templateStylingColorsItems, updatedTemplateStylingData);
    setColorsSectionItems(mappedColorsSectionSettings);

    setTemplateStylingData(updatedTemplateStylingData);
  };

  if (systemSettingsLoading) {
    return <LoaderOnWholeScreen size={60} />;
  }

  return (
    <>
      {editPageKey === 'logo' && <EditLogo currentRow={currentRow} saveSettingValue={saveSettingValue} handleClose={handleCloseEditPage} />}
      {(editPageKey === 'headerFontStyle' || editPageKey === 'bodyFontStyle') && (
        <EditFontStyle currentRow={currentRow} saveSettingValue={saveSettingValue} handleClose={handleCloseEditPage} />
      )}
      {editPageKey === 'watermark' && <EditWatermark currentRow={currentRow} saveSettingValue={saveSettingValue} handleClose={handleCloseEditPage} />}
      {editPageKey === 'disclosureText' && (
        <EditDisclosureText currentRow={currentRow} saveSettingValue={saveSettingValue} handleClose={handleCloseEditPage} />
      )}
      {editPageKey === 'accentColor' && (
        <EditAccentColor currentRow={currentRow} saveSettingValue={saveSettingValue} handleClose={handleCloseEditPage} />
      )}
      {editPageKey === 'default' && (
        <>
          <MainWrap>
            <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
            <PageTitle title="Templates" />
          </MainWrap>
          <MainWrap>
            <PaddingWrap>
              <SettingsContainer>
                <TierDropdown title="Template Styling">
                  {templateStylingSettingsItems.map((item) => (
                    <RowWrapper key={item.key} disabled={false} isMobile={isMobile}>
                      <InfoRowCell>{item.fieldName}</InfoRowCell>
                      {item.value ? (
                        <>{getTemplateStylingValue(item)}</>
                      ) : (
                        <EmptyTodoRowCell>{getTemplateStylingZeroState(item.key)}</EmptyTodoRowCell>
                      )}
                      <EditTodoButton isMobile={isMobile} id="editTodoButton" onClick={() => redirectToEditPage(item)}>
                        <EditTodoIcon />
                      </EditTodoButton>
                    </RowWrapper>
                  ))}
                </TierDropdown>
                <TierDropdown title="Colors">
                  {colorsSectionItems.map((item) => (
                    <RowWrapper key={item.key} disabled={false} isMobile={isMobile}>
                      <InfoRowCell>{item.fieldName}</InfoRowCell>
                      {item?.value ? (
                        <FieldValueWrapper>
                          <ColorPreviewWrapper>
                            <ColorPreview bgColor={item.value} />
                            {item.value}
                          </ColorPreviewWrapper>
                        </FieldValueWrapper>
                      ) : (
                        <EmptyTodoRowCell>To Do</EmptyTodoRowCell>
                      )}
                      <EditTodoButton isMobile={isMobile} id="editTodoButton" onClick={() => redirectToEditPage(item)}>
                        <EditTodoIcon />
                      </EditTodoButton>
                    </RowWrapper>
                  ))}
                </TierDropdown>
              </SettingsContainer>
            </PaddingWrap>
          </MainWrap>
        </>
      )}
    </>
  );
};

const SettingsContainer = styled.div`
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EditTodoButton = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  margin-left: auto;
  cursor: pointer;
  height: 40px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '40px')};
  background-color: ${themeVar('action.default')};
  opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
`;

const ImageWrapper = styled.div`
  width: 183px;
  height: 120px;
  overflow: hidden;
  border: 1px solid ${themeVar('action.active')};
  border-radius: 5px;
  padding: 10px;
`;

const ImageItem = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoZeroState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NoLogoText = styled.p`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.weak')};
`;

const LogoSpecifications = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const LogoSpecificationsUnorderedList = styled.ul`
  margin-left: 25px;
  list-style-type: square;
`;

const ColorPreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorPreview = styled.div<{ bgColor: string }>`
  width: 20px;
  height: 20px;
  border-radius: 2px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${({ bgColor }) => bgColor};
`;

const FontStyleWrapper = styled(FieldValueWrapper)<{
  fontFamily: string;
  fontColor: string;
  fontWeight: string;
}>`
  display: flex;
  align-items: center;
  font-family: ${({ fontFamily }) => fontFamily};
  color: ${({ fontColor }) => fontColor};
  font-weight: ${({ fontWeight }) => fontWeight};
`;

const FontStyleText = styled.span`
  display: inline;
`;

const Separator = styled.span`
  margin: 0 6px 0 2px;
`;
